import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './CTABannerSettings.css';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const emptyCta = {
  heading: '',
  subheading: '',
  buttonText: 'GET IN TOUCH',
  backgroundImage: '',
};

function CTABannerSettings() {
  const [cta, setCta] = useState(emptyCta);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchCta = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/cta-banner`);
        setCta({ ...emptyCta, ...response.data });
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCta();
  }, []);

  const handleChange = (field, value) => {
    setCta((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/cta-banner/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (response.data?.url) {
        handleChange('backgroundImage', response.data.url);
        setMessage('Background image uploaded. Click Save to apply.');
      }
    } catch (err) {
      console.error(err);
      setMessage('Unable to upload image. Please try again.');
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage('');

    try {
      await axios.put(`${API_BASE_URL}/api/cta-banner`, cta);
      setMessage('CTA banner updated successfully.');
    } catch (err) {
      console.error(err);
      setMessage('Unable to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <p className="cta-banner-settings-loading">Loading CTA banner…</p>
    );
  }

  return (
    <div className="cta-banner-settings">
      <div className="cta-banner-settings-header">
        <div>
          <h2 className="cta-banner-settings-title">CTA banner</h2>
          <p className="cta-banner-settings-subtitle">
            Edit the call-to-action section above the footer: heading, subheading,
            button text, and background image.
          </p>
        </div>
        <button
          type="button"
          className="cta-banner-settings-save-btn"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      <div className="cta-banner-settings-grid">
        <div className="cta-banner-settings-field cta-banner-settings-field-wide">
          <label className="cta-banner-settings-label">
            Heading
            <input
              type="text"
              className="cta-banner-settings-input"
              value={cta.heading}
              onChange={(e) => handleChange('heading', e.target.value)}
              placeholder="We Carry More Than Just Legal Expertise"
            />
          </label>
        </div>

        <div className="cta-banner-settings-field cta-banner-settings-field-wide">
          <label className="cta-banner-settings-label">
            Subheading
            <textarea
              className="cta-banner-settings-textarea"
              rows={2}
              value={cta.subheading}
              onChange={(e) => handleChange('subheading', e.target.value)}
              placeholder="We provide uncompromising dedication and absolute loyalty to your business interests."
            />
          </label>
        </div>

        <div className="cta-banner-settings-field">
          <label className="cta-banner-settings-label">
            Button text
            <input
              type="text"
              className="cta-banner-settings-input"
              value={cta.buttonText}
              onChange={(e) => handleChange('buttonText', e.target.value)}
              placeholder="GET IN TOUCH"
            />
          </label>
        </div>

        <div className="cta-banner-settings-field cta-banner-settings-field-wide">
          <label className="cta-banner-settings-label">
            Background image URL
            <input
              type="text"
              className="cta-banner-settings-input"
              value={cta.backgroundImage}
              onChange={(e) => handleChange('backgroundImage', e.target.value)}
              placeholder="https://example.com/cta-bg.jpg or leave empty to use default"
            />
          </label>
        </div>

        <div className="cta-banner-settings-field cta-banner-settings-field-wide">
          <label className="cta-banner-settings-label">
            Or upload background image
            <input
              type="file"
              accept="image/*"
              className="cta-banner-settings-input"
              onChange={handleImageUpload}
            />
          </label>
        </div>
      </div>

      {message && (
        <p className="cta-banner-settings-message">{message}</p>
      )}
    </div>
  );
}

export default CTABannerSettings;
